import React, { useState, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import {
  Modal,
  Tabs,
  Typography,
  Spin,
  Alert,
  Tag,
  Button,
  Descriptions,
  Table,
} from 'antd';
import {
  DollarOutlined,
  UnorderedListOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import {
  GET_POLICY_CONSUMPTION_DETAILS,
  GET_INSURANCE_CLAIMS_BY_POLICY,
} from '../../graphql/queries';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface PolicyBalanceDialogProps {
  visible: boolean;
  policyId: string | null;
  policyName?: string;
  policyNumber?: string;
  onClose: () => void;
  onOpenClaimDetail?: (claimId: string) => void;
}

const PolicyBalanceDialog: React.FC<PolicyBalanceDialogProps> = ({
  visible,
  policyId,
  policyName,
  policyNumber,
  onClose,
  onOpenClaimDetail,
}) => {
  const [activeTab, setActiveTab] = useState('balance');

  const [getPolicyConsumption, { data: consumptionData, loading: consumptionLoading }] = useLazyQuery(
    GET_POLICY_CONSUMPTION_DETAILS
  );

  const [getClaimsByPolicy, { data: claimsData, loading: claimsLoading }] = useLazyQuery(
    GET_INSURANCE_CLAIMS_BY_POLICY
  );

  // Fetch consumption data when balance tab is selected and dialog is visible
  useEffect(() => {
    if (visible && policyId && activeTab === 'balance') {
      getPolicyConsumption({ variables: { policyId } });
    }
  }, [visible, policyId, activeTab, getPolicyConsumption]);

  // Fetch claims when Claims tab is selected
  useEffect(() => {
    if (visible && policyId && activeTab === 'claims') {
      getClaimsByPolicy({ variables: { policyId } });
    }
  }, [visible, policyId, activeTab, getClaimsByPolicy]);

  const handleClose = () => {
    setActiveTab('balance');
    onClose();
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <DollarOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          Policy Details
        </div>
      }
      open={visible}
      onCancel={handleClose}
      footer={[
        <Button key="close" onClick={handleClose}>
          Close
        </Button>
      ]}
      width={900}
      destroyOnClose
    >
      {(policyName || policyNumber) && (
        <div style={{ marginBottom: 24 }}>
          {policyName && (
            <Title level={4} style={{ margin: 0 }}>
              {policyName}
            </Title>
          )}
          {policyNumber && (
            <Text type="secondary">
              Policy Number: {policyNumber}
            </Text>
          )}
        </div>
      )}

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane
          tab={
            <span>
              <DollarOutlined />
              Balance
            </span>
          }
          key="balance"
        >
          {consumptionLoading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Spin size="large" />
            </div>
          ) : consumptionData?.insuranceEligibilityCheckResult ? (
            <div style={{ padding: '16px 0' }}>
              <Descriptions bordered column={1}>
                {consumptionData.insuranceEligibilityCheckResult.coverageDetails?.remainingCoverageAmount && (
                  <Descriptions.Item label="Remaining Coverage Amount">
                    {Object.entries(consumptionData.insuranceEligibilityCheckResult.coverageDetails.remainingCoverageAmount).map(([key, value]) => (
                      <div key={key} style={{ marginBottom: 8 }}>
                        <Tag color="green" style={{ fontSize: '14px', padding: '4px 12px' }}>
                          {key}: ${String(value)}
                        </Tag>
                      </div>
                    ))}
                  </Descriptions.Item>
                )}
                {consumptionData.insuranceEligibilityCheckResult.copaymentAmount !== undefined && consumptionData.insuranceEligibilityCheckResult.copaymentAmount !== null && (
                  <Descriptions.Item label="Copayment Amount">
                    <Text strong style={{ fontSize: '16px' }}>${consumptionData.insuranceEligibilityCheckResult.copaymentAmount.toFixed(2)}</Text>
                  </Descriptions.Item>
                )}
                {consumptionData.insuranceEligibilityCheckResult.coverageDetails && (
                  <>
                    {consumptionData.insuranceEligibilityCheckResult.coverageDetails.freeConsultationsPerWeek !== undefined && (
                      <Descriptions.Item label="Remaining Free Consultations Per Week">
                        <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>
                          {consumptionData.insuranceEligibilityCheckResult.coverageDetails.freeConsultationsPerWeek}
                        </Text>
                      </Descriptions.Item>
                    )}
                    {consumptionData.insuranceEligibilityCheckResult.coverageDetails.freeConsultationsPerMonth !== undefined && (
                      <Descriptions.Item label="Remaining Free Consultations Per Month">
                        <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>
                          {consumptionData.insuranceEligibilityCheckResult.coverageDetails.freeConsultationsPerMonth}
                        </Text>
                      </Descriptions.Item>
                    )}
                    {consumptionData.insuranceEligibilityCheckResult.coverageDetails.freeConsultationsPerYear !== undefined && (
                      <Descriptions.Item label="Remaining Free Consultations Per Year">
                        <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>
                          {consumptionData.insuranceEligibilityCheckResult.coverageDetails.freeConsultationsPerYear}
                        </Text>
                      </Descriptions.Item>
                    )}
                    {consumptionData.insuranceEligibilityCheckResult.coverageDetails.freeConsultations !== undefined && (
                      <Descriptions.Item label="Remaining Total Free Consultations">
                        <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>
                          {consumptionData.insuranceEligibilityCheckResult.coverageDetails.freeConsultations}
                        </Text>
                      </Descriptions.Item>
                    )}
                  </>
                )}
              </Descriptions>
            </div>
          ) : (
            <Alert
              message="No consumption data available"
              type="info"
              showIcon
            />
          )}
        </TabPane>
        <TabPane
          tab={
            <span>
              <UnorderedListOutlined />
              Claims
            </span>
          }
          key="claims"
        >
          {claimsLoading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Spin size="large" />
            </div>
          ) : claimsData?.insuranceClaimResultsByPolicy && claimsData.insuranceClaimResultsByPolicy.length > 0 ? (
            <Table
              dataSource={claimsData.insuranceClaimResultsByPolicy}
              columns={[
                {
                  title: 'ID',
                  dataIndex: 'id',
                  key: 'id',
                  render: (text: string) => <Text code>{text.substring(0, 8)}...</Text>,
                },
                {
                  title: 'Status',
                  dataIndex: 'status',
                  key: 'status',
                  render: (status: string) => {
                    const colorMap: Record<string, string> = {
                      approved: 'green',
                      pending: 'orange',
                      rejected: 'red',
                    };
                    return (
                      <Tag color={colorMap[status.toLowerCase()] || 'default'}>
                        {status}
                      </Tag>
                    );
                  },
                },
                {
                  title: 'Approved Amount',
                  dataIndex: ['claimResultDetails', 'approved_amount'],
                  key: 'approvedAmount',
                  render: (amount: any) => {
                    if (amount == null || amount === '') return '-';
                    const numAmount = typeof amount === 'number' ? amount : parseFloat(amount);
                    return isNaN(numAmount) ? '-' : `$${numAmount.toFixed(2)}`;
                  },
                },
                {
                  title: 'Rejected Reason',
                  dataIndex: ['claimResultDetails', 'rejected_reason'],
                  key: 'rejectedReason',
                  render: (reason: string) => reason || '-',
                  ellipsis: true,
                },
                {
                  title: 'Items Count',
                  key: 'itemsCount',
                  render: (_: any, record: any) => record.items?.length || 0,
                },
                {
                  title: 'Created At',
                  dataIndex: 'createdAt',
                  key: 'createdAt',
                  render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm:ss'),
                },
                {
                  title: 'Actions',
                  key: 'actions',
                  fixed: 'right' as const,
                  render: (_: any, record: any) => (
                    <Button
                      type="link"
                      icon={<EyeOutlined />}
                      size="small"
                      onClick={() => onOpenClaimDetail?.(record.id)}
                      disabled={!onOpenClaimDetail}
                    >
                      View Details
                    </Button>
                  ),
                },
              ]}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              scroll={{ x: 'max-content' }}
              expandable={{
                expandedRowRender: (record: any) => (
                  <div style={{ padding: '16px' }}>
                    <Title level={5}>Claim Items</Title>
                    {record.items && record.items.length > 0 ? (
                      <Table
                        dataSource={record.items}
                        columns={[
                          {
                            title: 'Name',
                            dataIndex: 'name',
                            key: 'name',
                          },
                          {
                            title: 'Category',
                            dataIndex: 'category',
                            key: 'category',
                          },
                          {
                            title: 'Amount',
                            dataIndex: 'amount',
                            key: 'amount',
                            render: (amount: any) => {
                              if (amount == null || amount === '') return '-';
                              const numAmount = typeof amount === 'number' ? amount : parseFloat(amount);
                              return isNaN(numAmount) ? '-' : `$${numAmount.toFixed(2)}`;
                            },
                          },
                          {
                            title: 'Approved Amount',
                            dataIndex: 'approvedAmount',
                            key: 'approvedAmount',
                            render: (amount: any) => {
                              if (amount == null || amount === '') return '-';
                              const numAmount = typeof amount === 'number' ? amount : parseFloat(amount);
                              return isNaN(numAmount) ? '-' : `$${numAmount.toFixed(2)}`;
                            },
                          },
                          {
                            title: 'Transaction ID',
                            dataIndex: 'transactionId',
                            key: 'transactionId',
                            render: (id: string) => id ? <Text code>{id.substring(0, 8)}...</Text> : '-',
                          },
                        ]}
                        rowKey="id"
                        pagination={false}
                        size="small"
                      />
                    ) : (
                      <Text type="secondary">No items</Text>
                    )}
                  </div>
                ),
              }}
            />
          ) : (
            <Alert
              message="No claims found for this policy"
              type="info"
              showIcon
            />
          )}
        </TabPane>
      </Tabs>
    </Modal>
  );
};

export default PolicyBalanceDialog;

